import css from '../../index.module.scss';
import {
  Typography,
  Row,
  Col,
  Popover,
  Tooltip,
} from 'antd';
import {useStore} from '@/store/root-store';
import {AnalyticsCollapseCard} from '../../Common/AnalyticsCollapseCard';
import styles from '../style.module.scss';
import {useTranslation} from 'next-i18next';
import {getStatusColor} from '@/utils/colors';
import {NextImg} from '@/utils/nextImg';

import classNames from 'classnames';
import {Code, ExternalLink, Image,
  List,
  Video} from 'react-feather';
import {WarningMiniBar} from '@/components/common-components/components';
import {
  ButtonLink,
} from '@/components/common-components/components';
import {Button} from '@/components/common-components/v2';
import {useState} from 'react';

import {MixPanel} from '@/utils/mixpanel';
import {observer} from 'mobx-react-lite';
import {SCA_PAGE_EXTERNAL_LINKS_SHOW_BROKEN, SCA_PAGE_IMAGES_SHOW_MISSING_ALT, SCA_PAGE_INTERNAL_LINKS_SHOW_BROKEN} from '@/constants/events';
import {SchemaModal} from '../SchemaModal';
import {toJS} from 'mobx';
import {linkgraphDomain} from '@/utils/const';


export const TechnicalsData = observer(() => {
  const [showMissingAltText, setShowMissingAltText] = useState(false);
  const [isActiveInternalBrokenLinks, setIsActiveInternalBrokenLinks] = useState(false);
  const [isActiveExternalBrokenLinks, setIsActiveExternalBrokenLinks] = useState(false);
  const [showSchemaModal, setShowSchemaModal] = useState(false);


  const {
    contentOptimizer: {
      currentPage,
    }} = useStore('');


  const {Title} = Typography;
  const {t} = useTranslation('common');


  // const addDefaultSrc = e => {
  //   e.target.src = '/img/icon/broken-image.svg';
  // };

  const translateWord = (status: string) => {
    switch (status) {
      case 'Good':
        return t('good');
      case 'Average':
        return t('average');
      case 'Bad':
        return t('bad');
      case 'Missing':
        return t('missing');
      case 'Not Included':
        return 'Not Included';
      default:
        return '';
    }
  };

  const internalLinksFilters = [{
    label: 'Show only links with issues',
    disabledTooltip: 'There are no links with issues on this page',
    checked: isActiveInternalBrokenLinks,
    disabled: currentPage?.analysis?.links?.internal?.filter(link => link.statusCode && link.statusCode && link.statusCode > 299)?.length == 0,
    onChange: () => {
      setIsActiveInternalBrokenLinks(!isActiveInternalBrokenLinks);
      // monitoring for false value even tho we need true
      // the reason is that the event doesn't notice the change immediately
      if (!isActiveInternalBrokenLinks) {
        MixPanel.track(SCA_PAGE_INTERNAL_LINKS_SHOW_BROKEN, {
          'pageId': currentPage?.content.uuid,
          'description': 'Filtering for broken internal links',
        });
      }
    },
  }];

  const uiFormatImageSize = (size: string) => {
    if (size) {
      let returnSize = null;
      const sizeArray = size.split('x');
      returnSize = <div>{`x ${sizeArray[0]}`}<br />{sizeArray[1]}</div>;
      return returnSize;
    } else {
      return '-';
    }
  };

  const brokenHeaders = <div>{currentPage?.analysis?.brokenHeaders?.map((item, i) => {
    return <span key={i} style={{color: '#F44343'}}>{item.type}{(i + 1) < currentPage?.analysis?.brokenHeaders?.length && `, `}</span>;
  })}{translateWord(currentPage?.analysis?.status?.headers)}</div>;

  const getMissingAltText = () => {
    if (showMissingAltText && currentPage?.analysis?.images?.img) {
      return currentPage?.analysis.images.img.filter(image => !image.alt || image.alt === '');
    } else if (currentPage?.analysis?.images?.img) {
      return currentPage?.analysis.images.img;
    }
  };

  const getMissingAltTotal = () => {
    if (!currentPage?.analysis?.images?.img?.length) return null;
    if (currentPage?.analysis.images.img.filter(image => !image.alt || image.alt === '').length) {
      return currentPage?.analysis.images.img.filter(image => !image.alt || image.alt === '').length;
    } else {
      return null;
    }
  };

  const generateInternalLinksDesc = () => {
    const msgArr = [];
    let labelColor = '#fff';

    if (currentPage?.analysis?.getInternalBrokenLinksCount || currentPage?.analysis?.getInternalBrokenLinksCount === 0) {
      const msg = currentPage?.analysis?.getInternalBrokenLinksCount > 1 || currentPage?.analysis?.getInternalBrokenLinksCount == 0 ? 'link issues' : 'link issue';
      msgArr.push(`${currentPage?.analysis?.getInternalBrokenLinksCount} ${msg}`);
      if (currentPage?.analysis?.getInternalBrokenLinksCount > 0) {
        labelColor = '#F44343';
      }
    }

    if (currentPage?.analysis?.getInternalRedirectsCount || currentPage?.analysis?.getInternalRedirectsCount === 0) {
      const msg = currentPage?.analysis?.getInternalRedirectsCount > 1 || currentPage?.analysis?.getInternalRedirectsCount == 0 ? 'redirects' : 'redirect';
      msgArr.push(`${currentPage?.analysis?.getInternalRedirectsCount}  ${msg}`);
    }
    if (currentPage?.analysis?.links?.internal?.length) {
      return <>{msgArr[0] && <span style={{color: labelColor}}>{msgArr[0]}, </span>} {msgArr[1] && <span>{msgArr[1]}</span>}</>;
    } else {
      return '0';
    }
  };

  const imagesFilter = [{
    label: 'Show only missing alt text',
    checked: showMissingAltText,
    disabled: currentPage?.analysis?.images?.img?.filter(image => !image.alt || image.alt === '')?.length == 0,
    onChange: () => {
      setShowMissingAltText(!showMissingAltText);
      // monitoring for false value even tho we need true
      // the reason is that the event doesn't notice the change immediately
      if (!isActiveExternalBrokenLinks) {
        MixPanel.track(SCA_PAGE_IMAGES_SHOW_MISSING_ALT, {
          'pageId': currentPage?.content.uuid,
          'description': 'Filtering for images with missing alt text',
        });
      }
    },
  }];

  const externalLinksFilters = [{
    label: 'Show only links with issues',
    disabledTooltip: 'There are no links with issues on this page',
    checked: isActiveExternalBrokenLinks,
    disabled: currentPage?.analysis?.links?.external?.filter(link => link.statusCode && link.statusCode && link.statusCode > 299)?.length == 0,
    onChange: () => {
      setIsActiveExternalBrokenLinks(!isActiveExternalBrokenLinks);
      // monitoring for false value even tho we need true
      // the reason is that the event doesn't notice the change immediately
      if (!isActiveExternalBrokenLinks) {
        MixPanel.track(SCA_PAGE_EXTERNAL_LINKS_SHOW_BROKEN, {
          'pageId': currentPage?.content.uuid,
          'description': 'Filtering for broken external links',
        });
      }
    },
  }];

  const generateExternallLinksDesc = () => {
    const msgArr = [];
    let labelColor = '#fff';

    if (currentPage?.analysis?.getExternalBrokenCount || currentPage?.analysis?.getExternalBrokenCount === 0) {
      const msg = currentPage?.analysis?.getExternalBrokenCount > 1 || currentPage?.analysis?.getExternalBrokenCount == 0 ? 'link issues' : 'link issue';
      msgArr.push(`${currentPage?.analysis?.getExternalBrokenCount} ${msg}`);
      if (currentPage?.analysis?.getExternalBrokenCount > 0) {
        labelColor = '#F44343';
      }
    }

    if (currentPage?.analysis?.getExternalRedirectsCount || currentPage?.analysis?.getExternalRedirectsCount === 0) {
      const msg = currentPage?.analysis?.getExternalRedirectsCount > 1 || currentPage?.analysis?.getExternalRedirectsCount == 0 ? 'redirects' : 'redirect';
      msgArr.push(`${currentPage?.analysis?.getExternalRedirectsCount}  ${msg}`);
    }
    if (currentPage?.analysis?.links?.external?.length) {
      return <>{msgArr[0] && <span style={{color: labelColor}}>{msgArr[0]}, </span>} {msgArr[1] && <span>{msgArr[1]}</span>}</>;
    } else {
      return '0';
    }
  };

  const getExternalLinks = () => {
    if (isActiveExternalBrokenLinks && currentPage?.analysis?.links?.external) {
      return currentPage?.analysis.links.external?.slice()?.filter(link => link.statusCode && link.statusCode > 299);
    } else if (currentPage?.analysis?.links?.external) {
      return currentPage?.analysis.links.external?.slice()?.sort((a, b) => (a.statusCode > b.statusCode) ? -1 : ((b.statusCode > a.statusCode) ? 1 : 0));
    }
  };

  const getInternalLinks = () => {
    if (isActiveInternalBrokenLinks && currentPage?.analysis?.links?.internal) {
      return currentPage?.analysis.links.internal?.slice()?.filter(link => link.statusCode && link.statusCode > 299);
    } else if (currentPage?.analysis?.links?.internal) {
      return currentPage?.analysis.links.internal?.slice()?.sort((a, b) => (a.statusCode > b.statusCode) ? -1 : ((b.statusCode > a.statusCode) ? 1 : 0));
    }
  };

  const displayStatusCode = (statusCode: number) => {
    if (statusCode) {
      if (statusCode >= 200 && statusCode < 300) {
        return <span style={{color: '#80D08D'}}>{statusCode}</span>;
      } else if (statusCode >= 300 && statusCode < 400) {
        return <span style={{color: '#c1950e'}}>{statusCode}</span>;
      } else if (statusCode >= 400) {
        if (statusCode === 429) {
          return <span style={{color: '#ff8536'}}>{statusCode}</span>;
        } else {
          return <span style={{color: '#FF5656'}}>{statusCode}</span>;
        }
      }
    } else {
      return 'N/A';
    }
  };

  const displayFollowCode = (followCode: string) => {
    if (followCode) {
      if (followCode === 'nofollow') {
        return 'NF';
      } else {
        return <span style={{color: '#80D08D'}}>DF</span>;
      }
    } else {
      return '-';
    }
  };

  return <>
    <SchemaModal visible={showSchemaModal} setShowModal={setShowSchemaModal} schema={currentPage?.analysis?.schema?.jsonLd}/>
    <div style={{marginBottom: '40px'}}>
      <Title className={css.subTitle} level={4}>
        {t('head')}
      </Title>
      {currentPage?.analysis?.metaSummary?.map(meta => {
        return (
          <AnalyticsCollapseCard
            key={meta.field}
            className={styles.analyticsCollapseCardMargin}
            title={meta.field}
            description={meta.status}
            descriptionColor={meta.statusColor}
            footer={(
              <div>
                <div>{meta.value}</div>
                <div style={{fontSize: 12, color: meta.suggestionColor}}>{meta.suggestion}</div>
              </div>
            )}
          />
        );
      })}
      <div className={styles.divider}></div>
      <Title className={css.subTitle} level={4}>
        {t('rich-media-tags')}
      </Title>
      <AnalyticsCollapseCard
        className={styles.analyticsCollapseCardMargin}
        icon={<NextImg width={16} height={14} src='/icons/header.svg' />}
        title={t('headers')}
        description={brokenHeaders}
        descriptionColor={getStatusColor(currentPage?.analysis?.status?.headers)}
        colTitle1={t('tag')}
        colTitle2={{
          title: t('used'),
        }}
        data={currentPage?.analysis?.headers}
        dataRenderer={(header, i) => {
          return header.type === 'h1' || header.type === 'h2' || header.type === 'h3' ? header.count > 0 ? <Popover
            key={i}
            overlayClassName={classNames(styles.focusTermPopover, styles.headingsPopover)}
            trigger='hover'
            placement='left'
            autoAdjustOverflow={true}
            content={
              <div>
                {header.values.length && header.values.map((heading, i) => {
                  return <div key={`${heading}${i}`} className={styles.headingTooltipValue}><span style={{color: '#a3a4a4'}}> {`<${header.type}> `}</span>
                    {heading}
                    <span style={{color: '#a3a4a4'}}>{` </${header.type}>`}</span></div>;
                })}
              </div>
            }
          >
            <Row key={i} justify='space-between'>
              <Col>
                <div>{header.type.toUpperCase()}</div>
              </Col>
              <Col>
                <div style={{color: header.count === 0 && '#F44343'}}>{header.count}</div>
              </Col>
            </Row>
          </Popover> : <Row key={i} justify='space-between'>
            <Col>
              <div>{header.type.toUpperCase()}</div>
            </Col>
            <Col>
              <div style={{color: header.count === 0 && '#F44343'}}>{header.count}</div>
            </Col>
          </Row> : <Row key={i} justify='space-between'>
            <Col>
              <div>{header.type.toUpperCase()}</div>
            </Col>
            <Col>
              <div style={{color: header.count === 0 && '#F44343'}}>{header.count}</div>
            </Col>
          </Row>;
        }}
      />
      <AnalyticsCollapseCard
        className={styles.analyticsCollapseCardMargin}
        icon={<Image width={16} height={14} style={{color: '#A3A4A4'}}/>}
        title={t('images')}
        description={currentPage?.analysis?.images?.count || 0}
        isCollapse = {currentPage?.analysis?.images?.count > 0 ? true : false}
        colTitle1={t('src-alt')}
        colTitle2={{
          title: t('dim'),
        }}
        data={toJS(getMissingAltText())?.sort((a, b) => a.alt < b.alt ? -1 : 1)}
        filters={imagesFilter}
        warningBar={getMissingAltTotal() && <div style={{marginBottom: '10px'}}>
          <WarningMiniBar label={`${getMissingAltTotal()} images missing alt tag`}/>
        </div>}
        dataRenderer={(image, i) => {
          return (
            <>
              <Row key={i} justify='space-between' wrap={false} className={styles.focusTermContent} style={{flexDirection: 'row', marginBottom: '14px'}}>
                <Col span={3}>
                  <NextImg
                    src={image.src ? image.src : '/img/icon/broken-image.svg'}
                    alt={i}
                    style={{width: '30px', height: '30px', marginTop: '4px'}}
                    // onError={e => addDefaultSrc(e)}
                  />
                </Col>
                <Col span={14}>
                  {image.src ? !image.src.startsWith('data:') ? (
                    <a style={{display: 'block'}} className={classNames(styles.link, styles.truncateText)} href={image.src} target='_blank' rel='noreferrer'>{image.src}</a>
                  ) : (
                    <Tooltip title={'Click to copy link to clipboard.'}>
                      <div className={styles.truncateText} onClick={() => {
                        navigator.clipboard.writeText(image.src);
                      }}>{image.src}</div>
                    </Tooltip>
                  ) : '-'}
                  <div style={{fontSize: 12}}>{image.alt || '-'}</div>
                </Col>
                <Col span={7} style={{textAlign: 'right'}}>
                  {uiFormatImageSize(image.size)}
                </Col>
              </Row>
            </>
          );
        }}
        pageSize={5}
      />
      <AnalyticsCollapseCard
        className={styles.analyticsCollapseCardMargin}
        icon={<Video width={16} height={14} style={{color: '#A3A4A4'}}/>}
        title={t('videos')}
        description={currentPage?.analysis?.videos?.count || 0}
        isCollapse = {currentPage?.analysis?.videos?.count > 0 ? true : false}
        colTitle1={t('src-title')}
        colTitle2={{
          title: t('dim'),
        }}
        data={currentPage?.analysis?.videos?.video}
        dataRenderer={(video, i) => {
          return (
            <>
              <Row key={i} justify='space-between' wrap={false} className={styles.focusTermContent} style={{flexDirection: 'row'}}>
                <Col span={14}>
                  {video.visitable ? (
                    <a style={{display: 'block', fontSize: 12}} className={classNames(styles.link, styles.truncateText)} href={video.src} target='_blank' rel='noreferrer'>
                      {video.src}
                    </a>
                  ) : (
                    <div style={{fontSize: 12}} className={styles.truncateText}>{video.src}</div>
                  )}
                  <div style={{fontSize: 12}}>{video.title || t('no-title')}</div>
                </Col>
                <Col span={8} style={{textAlign: 'right', fontSize: 12}}>
                  {video.size}
                </Col>
              </Row>
            </>
          );
        }}
        pageSize={5}
      />
      <AnalyticsCollapseCard
        className={styles.analyticsCollapseCardMargin}
        icon={<Code width={16} height={14} style={{color: '#A3A4A4'}}/>}
        title={t('iframes')}
        description={currentPage?.analysis?.iframes?.count || 0}
        colTitle1={t('src-title')}
        data={currentPage?.analysis?.iframes?.iframe}
        isCollapse = {currentPage?.analysis?.iframes?.count > 0 ? true : false}
        dataRenderer={(iframe, i) => {
          return (
            <>
              <Row key={i} justify='space-between' wrap={false} className={styles.focusTermContent}>
                <Col>
                  {iframe.visitable ? (
                    <a style={{display: 'block'}} className={classNames(styles.link, styles.truncateText)} href={iframe.src} target='_blank' rel='noreferrer'>{iframe.src}</a>
                  ) : (
                    <div className={styles.truncateText}>{iframe.src}</div>
                  )}
                  <div style={{fontSize: 12}}>{iframe.title || t('no-title')}</div>
                </Col>
              </Row>
            </>
          );
        }}
        pageSize={5}
      />
      <AnalyticsCollapseCard
        className={styles.analyticsCollapseCardMargin}
        icon={<ExternalLink width={16} height={14} style={{color: '#A3A4A4'}}/>}
        title={t('internal-links')}
        description={generateInternalLinksDesc()}
        isCollapse = {currentPage?.analysis?.links?.internal?.length > 0 ? true : false}
        colTitle1={t('anchor-url')}
        colTitle2={{
          title: t('found-in'),
        }}
        colTitle3={t('follow').substring(0, 3) + '.'}
        colTitle4={t('status').substring(0, 4) + '.'}
        data={getInternalLinks()}
        filters={internalLinksFilters}
        dataRenderer={(link, i) => {
          return (
            <>
              <Row gutter={24} key={i} justify='space-between' className={styles.focusTermContent} style={{flexDirection: 'row', marginRight: '5px', marginBottom: '14px'}}>
                <Col span={15}>
                  {link.url ?
                    <a style={{display: 'block', fontSize: 12, textDecoration: 'none'}} className={classNames(styles.link, styles.truncateText)} href={link.url} target='_blank' rel='noreferrer'>
                      {link.url}
                    </a> : '-'}
                  <div style={{fontSize: 12}}>{link.anchor && link.anchor !== '\n\n' ? link.anchor.length > 27 ? link.anchor.substring(0, 27) + '...' : link.anchor : '-'}</div>
                </Col>
                <Col span={4} style={{padding: 0}}>
                  <p style={{fontSize: 12, textAlign: 'center'}}>{link.foundIn ? `${link.foundIn}` : '-'}</p>
                </Col>
                <Col span={2} style={{padding: 0}}>
                  <p style={{fontSize: 12, textAlign: 'center'}}>{displayFollowCode(link.rel[0])}</p>
                </Col>
                <Col span={3} style={{padding: 0}}>
                  <p style={{fontSize: 12, textAlign: 'center'}}>{displayStatusCode(link.statusCode)}</p>
                </Col>
              </Row>
            </>
          );
        }}
        pageSize={5}
      />
      <AnalyticsCollapseCard
        className={styles.analyticsCollapseCardMargin}
        icon={<ExternalLink width={16} height={14} style={{color: '#A3A4A4'}}/>}
        title={t('external-links')}
        description={generateExternallLinksDesc()}
        isCollapse = {currentPage?.analysis?.links?.external?.length > 0 ? true : false}
        colTitle1={t('anchor-url')}
        colTitle2={{
          title: t('found-in'),
        }}
        colTitle3={t('follow').substring(0, 3) + '.'}
        colTitle4={t('status').substring(0, 4) + '.'}
        data={getExternalLinks()}
        footer={currentPage?.analysis?.links?.external?.length > 0 ?( <></> ) : null}
        filters={externalLinksFilters}
        dataRenderer={(link, i) => {
          return (
            <>
              <Row gutter={24} key={i} justify='space-between' className={styles.focusTermContent} style={{flexDirection: 'row', marginRight: '5px', marginBottom: '14px'}}>
                <Col span={15}>
                  {link.url ?
                    <a style={{display: 'block', fontSize: 12, textDecoration: 'none'}} className={classNames(styles.link, styles.truncateText)} href={link.url} target='_blank' rel='noreferrer'>
                      {link.url}
                    </a> : '-'}
                  <div style={{fontSize: 12}}>{link.anchor && link.anchor !== '\n\n' ? link.anchor.length > 27 ? link.anchor.substring(0, 27) + '...' : link.anchor : '-'}</div>
                </Col>
                <Col span={4} style={{padding: 0}}>
                  <p style={{fontSize: 12, textAlign: 'center'}}>{link.foundIn ? `${link.foundIn}` : '-'}</p>
                </Col>
                <Col span={2} style={{padding: 0}}>
                  <p style={{fontSize: 12, textAlign: 'center'}}>{displayFollowCode(link.rel[0])}</p>
                </Col>
                <Col span={3} style={{padding: 0}}>
                  <p style={{fontSize: 12, textAlign: 'center'}}>{displayStatusCode(link.statusCode)}</p>
                </Col>
              </Row>
            </>
          );
        }}
        pageSize={5}
      />
      <div className={styles.divider}></div>
      <AnalyticsCollapseCard
        className={styles.analyticsCollapseCardMargin}
        icon={<List width={16} height={14} style={{color: '#A3A4A4'}}/>}
        title={t('schema')}
        description={currentPage?.analysis?.schema?.jsonLd ? t('validated') : 'Not Included'}
        descriptionColor={currentPage?.analysis?.schema?.jsonLd ? '#52D477' : '#A3A4A4'}
        colTitle1={currentPage?.analysis?.schema?.jsonLd && t('type')}
        colTitle2={{
          title: currentPage?.analysis?.schema?.jsonLd && t('status'),
        }}
        footer={currentPage?.analysis?.schema?.jsonLd ? (
          <>
            <Button
              variant='ghost'
              mode='dark'
              className={styles.customSchemaBtn}
              onClick={() => setShowSchemaModal(true)}>
              {t('view-schema-markups')}
            </Button>
          </>
        ) : (
          <ButtonLink
            href={`https://dashboard.${linkgraphDomain}/schema`}
            className={styles.viewSchemaBtn}
            buttonType='transparent'
            target='_blank'
            rel='noreferrer'
          >
            {t('view-schema-markups')}
          </ButtonLink>
        )
        }
      />
      <AnalyticsCollapseCard
        className={styles.analyticsCollapseCardMargin}
        icon={<NextImg width={16} height={14} src='/icons/puzzle.svg' />}
        title={t('social')}
        description={`${currentPage?.analysis?.social?.count || 0} ${t('sources')}`}
        isCollapse = {currentPage?.analysis?.social?.count > 0 ? true : false}
        colTitle1={t('source-url')}
        data={currentPage?.analysis?.social?.social}
        dataRenderer={(social, i) => {
          return (
            <>
              <Row key={i} justify='space-between' className={styles.focusTermContent}>
                <Col>
                  <div style={{textTransform: 'capitalize'}}>{social.type}</div>
                  <a className={classNames(styles.link, styles.truncateText)} target='_blank' rel='noreferrer' style={{fontSize: 12, display: 'block'}} href={social.url}>{social.url}</a>
                </Col>
              </Row>
            </>
          );
        }}
        pageSize={5}
      />
      <AnalyticsCollapseCard
        icon={<NextImg width={16} height={14} src='/icons/squares.svg' />}
        title={t('javascript')}
        description={currentPage?.analysis?.js?.count || 0}
        isCollapse = {currentPage?.analysis?.js?.count > 0 ? true : false}
        colTitle1={t('source')}
        data={currentPage?.analysis?.js?.js}
        dataRenderer={(js, i) => {
          return (
            <>
              <Row key={i} justify='space-between' className={styles.focusTermContent}>
                <Col>
                  {!js?.src?.startsWith('data:') ? (
                    <a style={{display: 'block'}} className={classNames(styles.link, styles.truncateText)} href={js.src} target='_blank' rel='noreferrer'>{js.src}</a>
                  ) : (
                    <Tooltip title={'Click to copy link to clipboard.'}>
                      <div className={styles.truncateText} onClick={() => {
                        navigator.clipboard.writeText(js.src);
                      }}>{js.src}</div>
                    </Tooltip>
                  )}
                </Col>
              </Row>
            </>
          );
        }}
        pageSize={5}
      />
    </div>
  </>;
});
